<template>
  <div class="emcd-puzzle">
    <div :style="wrapperStyle" ref="pictureNode" class="emcd-puzzle__wrapper">
      <img
        alt="part"
        class="emcd-puzzle__picture"
        :src="pictureLink"
        @load="getImageSize($event.target, 'picture')" />
      <img
        v-show="captchaStyle"
        class="emcd-puzzle__small"
        ref="partNode"
        :style="captchaStyle"
        :src="partLink"
        alt="part"
        @mousedown.stop="movePart($event, 'cursor')"
        @touchstart.stop="movePart($event, 'touch')"
        @load="getImageSize($event.target, 'part')"
        @touchend="getCoords"
        @mouseup="getCoords" />
    </div>
    <BaseButton primary size="md" @onClick="onSubmit">{{ $t("Send") }}</BaseButton>
  </div>
</template>
<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import BaseButton from "@emcd-shared/base-button";
import AnalyticsService from "@emcd-shared/analytics-service";

const props = defineProps({
  pictureBlob: {
    type: String,
    required: true,
  },
  partBlob: {
    type: String,
    required: true,
  },
  captchaSize: {
    type: String,
    default: () => "auto",
  },
});

const emit = defineEmits(["resolve"]);
const partCoords = ref(null);

// nodes
const partNode = ref(null);
const pictureNode = ref(null);

// sizes
const size = reactive({
  part: 0,
  picture: 0,
  wrapper: 0,
});
const ratio = computed(() => {
  if (!size.wrapper || !size.picture) {
    return null;
  }
  if (size.wrapper <= size.picture) {
    return size.wrapper / size.picture;
  }

  return 1;
});

const captchaStyle = computed(() => {
  const isInit = ratio.value;
  if (isInit) {
    return {
      width: `${size.part * ratio.value}px`,
      height: `${size.part * ratio.value}px`,
    };
  }
  return null;
});
const wrapperStyle = computed(() => {
  return {
    width: props.captchaSize,
    height: props.captchaSize,
  };
});

// img in base64
const pictureLink = computed(() => "data:image/png;base64," + props.pictureBlob);
const partLink = computed(() => "data:image/png;base64," + props.partBlob);

// get sizes
function getImageSize(node, variant) {
  const { naturalWidth } = node;
  size[variant] = naturalWidth;

  if (variant === "part") {
    getWrapperSize();
  }
}
function getWrapperSize() {
  const { offsetWidth } = pictureNode.value;
  size.wrapper = offsetWidth;
}

function getCoords() {
  const picture = pictureNode.value.getBoundingClientRect();
  const part = partNode.value.getBoundingClientRect();
  const coords = {
    x: Math.trunc((part.left - picture.left) / ratio.value),
    y: Math.trunc((part.top - picture.top) / ratio.value),
  };
  partCoords.value = btoa(JSON.stringify(coords));
}

function movePart(event, handlerType) {
  const moveAction = handlerType === "cursor" ? "mousemove" : "touchmove";
  const upAction = handlerType === "cursor" ? "mouseup" : "touchend";
  const eventX = event.clientX ?? event.touches[0].pageX;
  const eventY = event.clientY ?? event.touches[0].pageY;
  const xParent = pictureNode.value.getBoundingClientRect().left;
  const yParent = pictureNode.value.getBoundingClientRect().top;

  partNode.value.style.cursor = "grabbing";
  let shiftX = eventX - partNode.value.getBoundingClientRect().left;
  let shiftY = eventY - partNode.value.getBoundingClientRect().top;
  function moveAt(pageX, pageY) {
    partNode.value.style.left = pageX + "px";
    partNode.value.style.top = pageY + "px";
  }
  function onMouseMove(event) {
    const xClick = event.pageX ?? event.touches[0].pageX;
    const yClick = event.pageY ?? event.touches[0].pageY;

    const pageX = xClick - xParent - shiftX;
    const pageY = yClick - yParent - shiftY;

    moveAt(pageX, pageY);
  }
  function onMouseUp() {
    document.removeEventListener(moveAction, onMouseMove);
    partNode.value.removeEventListener(upAction, onMouseUp);
    partNode.value.onmouseup = null;
    partNode.value.style.cursor = "grab";
  }

  document.addEventListener(moveAction, onMouseMove);
  partNode.value.addEventListener(upAction, onMouseUp);

  partNode.value.ondragstart = function () {
    return false;
  };
}

function setPartRandomCoords() {
  partNode.value.style.left = Math.random() * 140 + "px";
  partNode.value.style.top = Math.random() * 140 + "px";
}

function onSubmit() {
  AnalyticsService.track("click", {
    event_type: "click",
    event_subtype: "captcha_success",
    product: "",
    screen: "",
    subscreen: "popup_captcha",
    button_name: "submit_captcha",
    event_content: {
      captcha_type: "emcd-puzzle",
    },
  });
  emit("resolve", partCoords.value);
}

onMounted(() => {
  setPartRandomCoords();
});
</script>

<style lang="scss" scoped>
.emcd-puzzle {
  display: flex;
  flex-direction: column;

  &__wrapper {
    position: relative;
    max-width: 300px;
    max-height: 300px;
    margin-bottom: 20px;

    @media (min-width: 768px) {
      max-width: 420px;
      max-height: 420px;
    }
  }

  &__small {
    position: absolute;
    cursor: grab;
    touch-action: none;
    border-radius: 12px;
    box-shadow: 0 0 15px 0 rgba(0, 0, 0, 0.5);
  }

  &__picture {
    pointer-events: none;
    -webkit-user-select: none; /* Safari */
    -moz-user-select: none; /* Firefox */
    -ms-user-select: none; /* IE10+/Edge */
    user-select: none; /* Standard */
    -webkit-touch-callout: none; /* iOS Safari */
    border-radius: 12px;
    border: 1px solid var(--bg-35);
  }
}
</style>
